import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  Divider,
  Flex,
  Footer,
  Heading,
  ProgressCircle,
  Text,
} from '@adobe/react-spectrum';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import Provider from '@react/react-spectrum/Provider';
import Textarea from '@react/react-spectrum/Textarea';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

import { Label, RedText } from './RatingsAndReviews';

const TextArea = styled(Textarea)`
  &&& {
    min-height: 135px;
    width: 100%;
  }
`;

const REPLY_MAX_CHARS = 2000;
const validateField = (newVal) => (newVal?.length >= 1 && newVal?.length <= REPLY_MAX_CHARS ? 'valid' : 'invalid');

export const ReplyDialog = (props) => {
  const { close, reviewId, variant, reply, addReply, editReply } = props;
  const [replyText, setReplyText] = useState({ value: reply?.text ?? '' });
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    if (errored) {
      setErrored(false);
    }
    setLoading(true);
    try {
      if (variant === 'edit') {
        await editReply(reply.id, replyText.value);
      } else {
        await addReply(reviewId, replyText.value);
      }
      close();
    } catch (err) {
      setErrored(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog size="M">
      <Heading>{variant === 'edit' ? 'Edit reply' : 'Reply'}</Heading>
      <Divider />
      <ButtonGroup>
        <Button variant="secondary" onPress={close} isDisabled={loading}>
          Cancel
        </Button>
        <Button
          autoFocus
          variant="cta"
          isDisabled={replyText.valid !== 'valid' || loading || replyText.value === reply?.text}
          onPress={onSubmit}
        >
          {loading && (
            <ProgressCircle size="S" isIndeterminate marginEnd="size-100" aria-label="Submitting your reply" />
          )}
          <Text>Save</Text>
        </Button>
      </ButtonGroup>
      <Content>
        <Provider>
          <Flex direction="column" width="100%">
            <Flex justifyContent="space-between" alignItems="center">
              <FieldLabel necessity="required" labelFor="reply-textarea" id="reply-textarea-label" label="Reply" />
              <Label>{REPLY_MAX_CHARS - replyText?.value?.length}</Label>
            </Flex>
            <TextArea
              autoFocus
              id="reply-textarea"
              value={replyText.value}
              validationState={replyText.valid}
              onChange={(value) => {
                const valid = validateField(value);
                setReplyText({ value, valid });
              }}
            />
          </Flex>
        </Provider>
      </Content>
      {errored && (
        <Footer>
          <RedText>Failed to save reply</RedText>
        </Footer>
      )}
    </Dialog>
  );
};

ReplyDialog.propTypes = {
  /** Function to close the dialog */
  close: PropTypes.func.isRequired,
  /** Whether you want to edit or add a reply */
  variant: PropTypes.oneOf(['edit', 'add']).isRequired,
  /** Id of the review you want to add a reply to */
  reviewId: PropTypes.string,
  /** Reply to be edited */
  reply: PropTypes.shape({ id: PropTypes.string, text: PropTypes.string }),
  editReply: PropTypes.func,
  addReply: PropTypes.func,
};
